import { useEffect, useRef } from 'react';

export default function VisionMission() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-item').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const missionPoints = [
    'Connect global producers and consumers through efficient and transparent trading solutions',
    'Maintain high standards of quality, logistics, and regulatory compliance',
    'Support responsible sourcing and sustainable supply chains',
    'Build long-term partnerships based on trust, performance, and reliability',
  ];

  return (
    <section id="vision" className="bg-[#0D1B2A] py-28" ref={sectionRef}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Section Label */}
        <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-16 text-center">
          <span className="inline-block border border-[#888888]/40 rounded-full px-6 py-2 text-[#A0A0A0] text-xs uppercase tracking-[0.3em]">
            Our Direction
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Vision */}
          <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full border border-[#888888]/50 bg-[#4A4A4A]/30">
                <i className="ri-eye-line text-[#A0A0A0] text-xl" />
              </div>
              <h2 className="text-3xl md:text-4xl font-playfair text-white">Vision</h2>
            </div>
            <div className="h-px w-16 bg-[#888888] mb-8" />
            <p className="text-[#B0B0B0] text-lg leading-relaxed">
              To be a trusted global partner in the sustainable and responsible trade of metals, minerals, fertilizers, and agricultural commodities, contributing to economic development and global food supply chains.
            </p>

            {/* Visual accent */}
            <div className="mt-10 relative overflow-hidden rounded-2xl">
              <img
                src="https://static.readdy.ai/image/969424c839acaee4bc2648b4f6dd384f/a0c1eec10be46db33dcb07a07dc06540.webp"
                alt="Hong Kong Global Trading Hub"
                className="w-full h-56 object-cover object-top rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/60 to-transparent rounded-2xl" />
              <div className="absolute bottom-4 left-4 text-[#A0A0A0] text-xs uppercase tracking-widest">Hong Kong — Global Hub</div>
            </div>
          </div>

          {/* Mission */}
          <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full border border-[#888888]/50 bg-[#4A4A4A]/30">
                <i className="ri-focus-3-line text-[#A0A0A0] text-xl" />
              </div>
              <h2 className="text-3xl md:text-4xl font-playfair text-white">Mission</h2>
            </div>
            <div className="h-px w-16 bg-[#888888] mb-8" />
            <div className="space-y-5">
              {missionPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#888888] text-lg font-bold font-playfair">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-[#B0B0B0] leading-relaxed group-hover:text-white transition-colors duration-300">{point}</p>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className="mt-10 pl-6 border-l-2 border-[#888888]">
              <p className="text-[#A0A0A0] text-xl font-playfair italic leading-relaxed">
                Building long-term partnerships through integrity, performance, and responsible business practices.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
